import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, Plus, X, Loader2, Pencil } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatUSD } from '../../lib/utils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AccountSelector = ({
  label,
  value = null,
  onChange,
  accounts = null,     // Static array mode (simple Select dropdown)
  fetchUrl = null,     // API search mode
  fetchParams = {},
  placeholder = 'Search...',
  showBalance = true,
  disabled = false,
  required = false,
  organizationId = null,
  quickAddType = null,
  onAddClick,
  onEditClick,
}) => {
  // === STATIC MODE: simple Select dropdown when `accounts` array is provided ===
  if (accounts) {
    return (
      <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {accounts.map(acc => (
            <SelectItem key={acc.id} value={acc.id}>
              <span className="flex items-center gap-2">
                <span>{acc.code} - {acc.name}</span>
                {showBalance && acc.balance_usd != null && (
                  <span className={`text-[10px] px-1 rounded ${
                    (acc.balance_usd || 0) >= 0 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    €{formatUSD(Math.abs(acc.balance_usd || 0))}
                  </span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // === SEARCH MODE: fetchUrl-based with search input ===
  return <SearchableAccountSelector
    label={label} value={value} onChange={onChange}
    fetchUrl={fetchUrl} fetchParams={fetchParams}
    placeholder={placeholder} showBalance={showBalance}
    disabled={disabled} required={required}
    organizationId={organizationId} quickAddType={quickAddType}
    onAddClick={onAddClick} onEditClick={onEditClick}
  />;
};

const SearchableAccountSelector = ({
  label, value, onChange, fetchUrl, fetchParams,
  placeholder, showBalance, disabled, required,
  organizationId, quickAddType, onAddClick, onEditClick,
}) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load selected account name on mount
  useEffect(() => {
    if (value && !selectedAccount && fetchUrl) {
      const params = new URLSearchParams({ ...fetchParams });
      axios.get(`${API}/${fetchUrl}?${params}`)
        .then(res => {
          const acc = (res.data || []).find(a => a.id === value || a.code === value);
          if (acc) setSelectedAccount(acc);
        })
        .catch(() => {});
    }
    if (!value) setSelectedAccount(null);
  }, [value, fetchUrl]);

  // Search with debounce
  useEffect(() => {
    if (!isOpen || !fetchUrl) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ ...fetchParams });
        if (search.trim()) params.append('search', search.trim());
        const res = await axios.get(`${API}/${fetchUrl}?${params}`);
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(timer);
  }, [search, isOpen, fetchUrl]);

  const handleSelect = (acc) => {
    setSelectedAccount(acc);
    onChange(acc.id || acc.code);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    setSelectedAccount(null);
    onChange(null);
    setSearch('');
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {selectedAccount ? (
        <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-muted/30 h-9">
          <span className="font-mono text-xs text-muted-foreground">{selectedAccount.code}</span>
          <span className="text-sm flex-1 truncate">{selectedAccount.name}</span>
          {!disabled && onEditClick && (
            <button onClick={() => onEditClick(selectedAccount)}
              className="text-blue-500 hover:text-blue-700" title="Edit"
              data-testid={`edit-${quickAddType || 'account'}-btn`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {!disabled && (
            <button onClick={handleClear} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              className="pl-9 h-9"
              data-testid={`account-selector-${quickAddType || 'search'}`}
            />
          </div>
          {onAddClick && !disabled && (
            <button onClick={onAddClick}
              className="h-9 w-9 flex items-center justify-center rounded-md border border-dashed border-primary/40 text-primary hover:bg-primary/10 shrink-0 transition-colors"
              title={`Add New ${quickAddType === 'supplier' ? 'Supplier' : 'Customer'}`}
              data-testid={`add-${quickAddType || 'account'}-btn`}>
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {isOpen && !selectedAccount && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-auto">
          {loading ? (
            <div className="p-3 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading...</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">No results found</div>
          ) : (
            results.slice(0, 30).map(acc => (
              <div key={acc.id || acc.code}
                className="px-3 py-2 hover:bg-muted cursor-pointer flex items-center gap-2 text-sm border-b last:border-0"
                onMouseDown={() => handleSelect(acc)}>
                <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{acc.code}</span>
                <span className="flex-1 truncate">{acc.name}</span>
                {showBalance && acc.balance_usd != null && (
                  <span className="text-xs text-muted-foreground">€{Number(acc.balance_usd || 0).toLocaleString()}</span>
                )}
                {onEditClick && (
                  <button
                    className="text-blue-500 hover:text-blue-700 p-0.5 rounded hover:bg-blue-50 shrink-0"
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onEditClick(acc); setIsOpen(false); }}
                    title="Edit"
                    data-testid={`edit-${quickAddType || 'account'}-${acc.code}`}>
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AccountSelector;
